<!-- components/DocContent.vue -->
<template>
  <div class="doc-content">
    <div v-if="loading" class="loading">
      加载中...
    </div>
    <div v-else-if="error" class="error">
      文档加载失败：{{ error }}
    </div>
    <div v-else>
      <h1 class="doc-title">{{ title }}</h1>
      <div v-html="renderedContent"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { marked } from 'marked'
import hljs from 'highlight.js'

export default {
  name: 'DocContent',
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore()
    const content = ref('')
    const title = ref('')
    const loading = ref(true)
    const error = ref(null)
    
    // 配置marked，添加代码高亮
    marked.setOptions({
      highlight: function(code, lang) {
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
      langPrefix: 'hljs language-'
    });
    
    // 渲染Markdown内容
    const renderedContent = computed(() => {
      return marked.parse(content.value);
    });

    const fetchDoc = async (slug) => {
      loading.value = true
      error.value = null
      
      try {
        const response = await fetch(`/docs/${slug}.md`)
        
        if (!response.ok) {
          throw new Error(`文档不存在 (${response.status})`)
        }
        
        content.value = await response.text()
        
        // 从store获取标题
        const doc = store.getters.getDocBySlug(slug)
        title.value = doc ? doc.title : slug
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchDoc(props.slug)
    })

    watch(() => props.slug, (newSlug) => {
      fetchDoc(newSlug)
    })

    return { content, title, loading, error, renderedContent }
  }
}
</script>

<style scoped>
.doc-content {
  max-width: 800px;
  margin: 0 auto;
}

.doc-title {
  margin-bottom: 30px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.loading, .error {
  padding: 20px;
  text-align: center;
  color: #6c757d;
}

.error {
  color: #dc3545;
}

/* Markdown内容样式 */
:deep(h2) {
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

:deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(p) {
  margin-bottom: 1rem;
}

:deep(ul), :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

:deep(li) {
  margin-bottom: 0.5rem;
}

:deep(code) {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: Consolas, Monaco, 'Andale Mono', monospace;
  font-size: 0.9em;
}

:deep(pre) {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 5px;
  overflow-x: auto;
  margin-bottom: 1rem;
}

:deep(pre code) {
  background: none;
  padding: 0;
}

:deep(blockquote) {
  border-left: 4px solid var(--primary-color);
  padding-left: 1rem;
  color: #6c757d;
  margin: 1rem 0;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

:deep(th), :deep(td) {
  border: 1px solid #eee;
  padding: 0.5rem;
}

:deep(th) {
  background: #f8f9fa;
}

/* 添加图片响应式样式 */
:deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1rem auto;
  border-radius: 5px;
  object-fit: contain;
}

/* 确保内容不会超出容器宽度 */
:deep(*) {
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .doc-content {
    padding: 0 15px;
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
  }
  
  :deep(img) {
    width: 100% !important; /* 强制图片在移动端适应容器宽度 */
    max-width: calc(100vw - 30px) !important; /* 考虑内边距 */
    position: relative;
    left: 0;
  }
  
  :deep(pre) {
    max-width: calc(100vw - 30px);
    white-space: pre-wrap;
  }
  
  :deep(table) {
    display: block;
    overflow-x: auto;
    max-width: calc(100vw - 30px);
  }
}
</style>